import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { assetUrl } from "@/lib/api";
import { SERVICES, MARQUEE_ITEMS } from "@/data/services";
import { Eyebrow, FadeUp, MaskedLines, EASE } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";

const ServiceCard = ({ s, className = "", featured = false, delay = 0 }) => {
  const Icon = s.icon;
  const { settings } = useSettings();
  const img = assetUrl(settings?.images?.[`service-${s.key}`] || settings?.images?.[s.imageKey]);
  return (
    <FadeUp delay={delay} className={`${className} h-full`}>
      <motion.div
        whileHover={{ y: -5 }}
        transition={{ duration: 0.3, ease: EASE }}
        className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-ink2/70 transition-colors hover:border-cyan-400/40 hover:shadow-[0_20px_60px_-20px_rgba(56,189,248,0.35)]"
        data-testid={`service-card-${s.key}`}
      >
        <img src={img} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-25 transition duration-700 group-hover:scale-105 group-hover:opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/40" />
        <div className="relative flex h-full flex-col p-6 lg:p-8">
          <Icon className="h-7 w-7 text-cyan-400" />
          <h3 className={`mt-4 font-display font-bold text-white ${featured ? "text-2xl lg:text-3xl" : "text-xl"}`}>{s.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.blurb}</p>
          <div className="mt-auto pt-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 transition group-hover:text-cyan-400">{s.tag}</span>
          </div>
        </div>
      </motion.div>
    </FadeUp>
  );
};

const Hero = () => {
  const { settings } = useSettings();
  const img = assetUrl(settings?.images?.hero);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="home-hero">
      <div aria-hidden className="pointer-events-none absolute -right-48 -top-48 h-[620px] w-[620px] rounded-full border border-cyan-400/10 animate-spin-slow" />
      <div aria-hidden className="pointer-events-none absolute -right-24 top-28 h-[400px] w-[400px] rounded-full border border-indigo-400/15" />
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-0 h-px w-[80%] -translate-x-1/2 bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />

      <div className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-12 lg:pb-24 lg:pt-16">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400"
            data-testid="hero-eyebrow"
          >
            Cyber Planet Inc — Digital · Media · Events
          </motion.p>
          <h1 className="mt-6 font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="hero-title">
            <MaskedLines
              baseDelay={0.25}
              lines={[
                <>We build brands</>,
                <>the world</>,
                <span className="bg-gradient-to-r from-cyan-300 to-indigo-400 bg-clip-text text-transparent">remembers.</span>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            className="mt-6 max-w-xl text-base text-slate-400 sm:text-lg"
            data-testid="hero-subline"
          >
            Strategy, software, campaigns and stages — an all-in-one family of partners, dreamers and builders making your project impossible to ignore.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-start-project-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3.5 text-sm font-semibold text-ink transition hover:bg-cyan-300"
            >
              Start a project
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-explore-services-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-white transition hover:border-cyan-400/50 hover:text-cyan-300"
            >
              Explore services
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-3 border-t border-white/5 pt-6 font-mono text-xs uppercase tracking-[0.2em] text-slate-500"
            data-testid="hero-trust-row"
          >
            <span>24/7 Support</span>
            <span>Global data centers</span>
            <span>All-in-One solutions</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y }} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.4, ease: EASE }} className="relative">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_120px_-30px_rgba(56,189,248,0.35)]" data-testid="hero-image-frame">
              <img src={img} alt="Cyber Planet — digital agency" className="h-[420px] w-full object-cover lg:h-[540px]" data-testid="hero-image" />
              <div aria-hidden className="absolute inset-x-0 -top-20 h-48 bg-cyan-400/20 blur-3xl" />
              <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 right-5 rounded-full border border-white/15 bg-ink/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 backdrop-blur-md">
                Est. for dreamers
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.05, ease: EASE }}
              className="glass absolute -bottom-7 -left-4 rounded-2xl border border-white/10 px-6 py-4 shadow-xl sm:-left-8"
              data-testid="hero-promise-badge"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">Our promise</p>
              <p className="mt-1 font-display font-semibold text-white">Changing the world for you!</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Bento = () => (
  <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24" data-testid="home-services-bento">
    <div className="flex flex-wrap items-end justify-between gap-6">
      <div>
        <Eyebrow>What we do</Eyebrow>
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
          Eight disciplines.
          <br />
          <span className="text-slate-500">One planet.</span>
        </h2>
      </div>
      <Link to="/services" className="group inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 transition hover:text-cyan-300" data-testid="bento-all-services-link">
        All services
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </div>
    <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-12 lg:gap-6">
      <ServiceCard s={SERVICES[1]} featured className="md:col-span-7 min-h-[300px]" delay={0} />
      <ServiceCard s={SERVICES[0]} className="md:col-span-5 min-h-[300px]" delay={0.08} />
      {SERVICES.slice(2).map((s, i) => (
        <ServiceCard key={s.key} s={s} className="md:col-span-4 min-h-[240px]" delay={0.06 * i} />
      ))}
    </div>
  </section>
);

const Approach = () => {
  const steps = [
    { n: "01", t: "Dream it", d: "Share the dream — your project, your audience, your ambition. We listen first." },
    { n: "02", t: "Build it", d: "One team of partners designs, programs and produces everything your project needs." },
    { n: "03", t: "Scale it", d: "We measure what matters and scale the channels that work — all-in-one, always on." },
  ];
  return (
    <section className="border-y border-white/5 bg-[#0a0e18]" data-testid="home-approach">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
        <Eyebrow>How we work</Eyebrow>
        <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-3">
          {steps.map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.1} className="relative">
              <span className="font-display text-6xl font-extrabold text-stroke">{s.n}</span>
              <h3 className="mt-4 font-display text-2xl font-bold text-white">{s.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.d}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

const EventsTeaser = () => {
  const { settings } = useSettings();
  const img = assetUrl(settings?.images?.concert);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="home-events-teaser">
      <motion.img src={img} alt="" aria-hidden style={{ y }} className="absolute inset-0 h-[120%] w-full object-cover" />
      <div aria-hidden className="absolute inset-0 bg-ink/75" />
      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-36">
        <FadeUp>
          <Eyebrow>Events & Concerts</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Let us rock the show.
          </h2>
          <p className="mt-5 max-w-xl text-base text-slate-300 sm:text-lg">
            Stage, sound, lighting and artists — we got it all to make your concert or corporate event a success.
          </p>
          <Link
            to="/events"
            data-testid="events-teaser-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-ink transition hover:bg-cyan-300"
          >
            Explore events <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

const CTABand = () => (
  <section className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 lg:px-12 lg:py-28" data-testid="home-cta-band">
    <FadeUp>
      <h2 className="font-display text-5xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl">
        Dare to <span className="bg-gradient-to-r from-cyan-300 to-indigo-400 bg-clip-text text-transparent">dream?</span>
      </h2>
      <p className="mx-auto mt-5 max-w-lg text-base text-slate-400 sm:text-lg">Dream big. We will help you along the way — from first sketch to full spotlight.</p>
      <Link
        to="/contact"
        data-testid="home-cta-contact-button"
        className="mt-9 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-8 py-4 text-sm font-semibold text-ink transition hover:bg-cyan-300"
      >
        Let's talk <ArrowRight className="h-4 w-4" />
      </Link>
    </FadeUp>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <Bento />
      <Approach />
      <EventsTeaser />
      <CTABand />
    </>
  );
}
